/**
 * Singleton loader for the Google Maps JS API.
 *
 * Multiple components may request Maps concurrently on the same page —
 * e.g. the "Requests by Location" panel plus a future admin geofence
 * picker — so the loader is idempotent:
 *
 *   • One global `<script>` tag ever gets injected per page.
 *   • All callers await the same resolved `Promise<typeof google.maps>`.
 *   • Error state is sticky so a rejected load doesn't keep retrying
 *     (fix the key, refresh the page).
 *
 * The API key comes from the server's `/system/client/config` response
 * (`googleMapsBrowserJSApiKey`) — callers must supply it explicitly so
 * this module stays Redux-store agnostic.
 *
 * Note: this loader used to request the `visualization` library to get
 * `google.maps.visualization.HeatmapLayer`. That layer was deprecated
 * in May 2025 and is being removed in May 2026, so we now ship a
 * custom canvas-based heatmap (see `heatmapCanvasOverlay.ts`). The
 * core `google.maps` namespace is all we need.
 */

/**
 * We don't ship `@types/google.maps` — keep the namespace typed as `any`
 * and let the one consumer (<GeoHeatmapPanel />) narrow as needed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GoogleMapsNamespace = any;

interface LoaderState {
  key: string | null;
  promise: Promise<GoogleMapsNamespace> | null;
}

const state: LoaderState = { key: null, promise: null };

const CALLBACK_NAME = '__infralytiqs_gmaps_cb__';
const SCRIPT_ID = 'infralytiqs-gmaps-loader';

export const loadGoogleMaps = (apiKey: string): Promise<GoogleMapsNamespace> => {
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is missing'));
  }

  // A Maps instance is already fully loaded on the page (either from a
  // previous call or a third-party SDK) — reuse it as-is. We only need
  // the core `OverlayView` to be available, which ships with the base
  // bundle (no extra library required).
  if (typeof window !== 'undefined' &&
      (window as any).google?.maps?.OverlayView) {
    return Promise.resolve((window as any).google.maps as GoogleMapsNamespace);
  }

  if (state.promise && state.key === apiKey) {
    return state.promise;
  }

  // A previous load was started with a *different* key — refuse rather
  // than silently mixing keys. The page must be reloaded to switch keys.
  if (state.promise && state.key && state.key !== apiKey) {
    return Promise.reject(new Error(
      'Google Maps was already initialized with a different API key.',
    ));
  }

  state.key = apiKey;
  state.promise = new Promise<GoogleMapsNamespace>((resolve, reject) => {
    (window as any)[CALLBACK_NAME] = () => {
      try { delete (window as any)[CALLBACK_NAME]; } catch { /* noop */ }
      const maps = ((window as any).google?.maps) as GoogleMapsNamespace | undefined;
      if (!maps?.OverlayView) {
        reject(new Error('Google Maps loaded but the core API is incomplete.'));
        return;
      }
      resolve(maps);
    };

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) return; // its onload will invoke our callback.

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    // No `&libraries=visualization` — the deprecated HeatmapLayer was
    // its only consumer in this app and has been replaced by our own
    // canvas overlay (see heatmapCanvasOverlay.ts).
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      `&callback=${CALLBACK_NAME}&v=weekly`;
    script.onerror = () => reject(new Error('Failed to download the Google Maps JS API.'));
    document.head.appendChild(script);
  });

  return state.promise;
};
